import apiClient from './client'

export type PersonStatus = 'Private' | 'Public' | 'Inactive'

export const PERSON_STATUSES: PersonStatus[] = ['Private', 'Public', 'Inactive']

export type ProfileType = 'BK' | 'Guest'

export const PROFILE_TYPES: ProfileType[] = ['BK', 'Guest']

export type BulkTagMode = 'replace' | 'add'

export interface Person {
  id: number
  first_name?: string
  last_name?: string
  email?: string
  phone?: string
  title?: string
  status: PersonStatus
  update_date?: string
  last_activity_date?: string
  user_id?: number
  full_name: string
  display_name: string
  // Related data counts
  biodata_count?: number
  photo_count?: number
  media_release_count?: number
  // URL of the thumbnail photo, included inline to avoid per-row fetches.
  thumbnail_photo_url?: string
  profile_type?: ProfileType | ''
  tags: string[]
}

export interface PersonCreateRequest {
  first_name?: string
  last_name?: string
  email?: string
  phone?: string
  title?: string
  status?: PersonStatus
  profile_type?: ProfileType | ''
  tags?: string[]
}

export interface PersonUpdateRequest {
  first_name?: string
  last_name?: string
  email?: string
  phone?: string
  title?: string
  status?: PersonStatus
  profile_type?: ProfileType | ''
  tags?: string[]
}

export type ProfileTypeFilter = ProfileType | 'Unassigned'

export interface PersonSearchParams {
  page?: number
  size?: number
  search?: string
  status?: PersonStatus
  profile_type?: ProfileTypeFilter
  sort_by?: string
  sort_order?: string
}

export interface PersonsResponse {
  items: Person[]
  total: number
  page: number
  size: number
  pages: number
  has_next: boolean
  has_prev: boolean
}

export interface PersonStats {
  total: number
  by_status: Record<string, number>
  by_profile_type: Record<string, number>
}

export const personApi = {
  getPersons: async (params?: PersonSearchParams): Promise<PersonsResponse> => {
    const response = await apiClient.get('/persons', { params })
    return response.data
  },

  getPerson: async (id: number): Promise<Person> => {
    const response = await apiClient.get(`/persons/${id}`)
    return response.data
  },

  createPerson: async (data: PersonCreateRequest): Promise<Person> => {
    const response = await apiClient.post('/persons', data)
    return response.data
  },

  updatePerson: async (id: number, data: PersonUpdateRequest): Promise<Person> => {
    const response = await apiClient.put(`/persons/${id}`, data)
    return response.data
  },

  deletePerson: async (id: number): Promise<void> => {
    await apiClient.delete(`/persons/${id}`)
  },

  searchPersons: async (query: string, limit = 10): Promise<Person[]> => {
    const response = await apiClient.get('/persons/search', {
      params: { q: query, limit }
    })
    return response.data
  },

  getStats: async (): Promise<PersonStats> => {
    const { data } = await apiClient.get('/persons/stats')
    return data
  },

  getMatchingIds: async (params?: { search?: string; status?: PersonStatus; profile_type?: ProfileTypeFilter }): Promise<number[]> => {
    const response = await apiClient.get('/persons/ids', { params })
    return response.data.ids
  },

  bulkUpdateStatus: async (personIds: number[], status: PersonStatus): Promise<{ success: boolean; updated: number; status: PersonStatus }> => {
    const response = await apiClient.post('/persons/bulk-status', {
      person_ids: personIds,
      status,
    })
    return response.data
  },

  bulkUpdateProfileType: async (personIds: number[], profileType: ProfileType | ''): Promise<{ success: boolean; updated: number; profile_type: string }> => {
    const response = await apiClient.post('/persons/bulk-profile-type', {
      person_ids: personIds,
      profile_type: profileType,
    })
    return response.data
  },

  bulkSetTags: async (personIds: number[], tags: string[], mode: BulkTagMode): Promise<{ success: boolean; updated: number; mode: BulkTagMode }> => {
    const response = await apiClient.post('/persons/bulk-tags', {
      person_ids: personIds,
      tags,
      mode,
    })
    return response.data
  },
}
